import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  AfterViewInit,
  Output,
  ViewChild,
  ViewChildren,
  QueryList,
  NgZone,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, ValidatorFn, Validators } from '@angular/forms';
import * as _ from 'lodash';
import { fromEvent, Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

import { AppFilter, AuthPermissions, SortOption } from '../../_models/models';
import { SharedService } from '../../_services/shared.service';
import { AppFilterButton } from './models';

@Component({
  selector: 'app-filters',
  templateUrl: './app-filters.component.html',
  styleUrls: ['./app-filters.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppFiltersComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChildren('appInput') appInputs: QueryList<ElementRef>;
  @ViewChild('filterButtons', { static: true }) filterButtons: ElementRef;
  @Input() buttons: AppFilterButton[] = [];
  @Input() isDataLoading = false;
  @Input() isDownloading = false;
  @Input() authPermissions: AuthPermissions;
  @Input() formValidatorFn: ValidatorFn;
  @Input() collapsibleFilters: string[] = [];
  isExpanded = false;


  @Output() filtersChanged: EventEmitter<{ filters: AppFilter[]; filterId: string }> = new EventEmitter();
  @Output() filterSearched: EventEmitter<any> = new EventEmitter();
  @Output() buttonClicked: EventEmitter<any> = new EventEmitter();
  filterButtonsColStart: string | number = 'auto';
  filtersControls: UntypedFormArray;
  updateFilterDebounce = _.debounce((filterId: string) => {
    this.filtersChanged.emit({ filters: this.filters, filterId: filterId });
  }, 300, {});

  searchFilterDebounce = _.debounce((searchValue: string, filterId: string) => {
    this.filterSearched.emit({ searchValue: searchValue, filterId: filterId });
  }, 1000, {});

  // debounce on button click is needed to allow state to update filters before
  clickButtonDebounce = _.debounce((buttonId: string) => {
    this.buttonClicked.emit(buttonId);
  }, 400, {});
  // input set-get approach, because simple changes won't capture previous filters
  // the idea is to manually check what changes should be applied to control form
  private unsubscribe$ = new Subject<void>();
  private _previousFilters: AppFilter[];
  private _currentFilters: AppFilter[];

  // Add this for the collapse panel icon template
  private expandedIcon = {
    rotate: 0
  };

  constructor(
    private formBuilder: UntypedFormBuilder,
    private sharedService: SharedService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {
  }
  get hasCollapsibleFilters(): boolean {
    return this.collapsibleFilters?.length > 0;
  }

  get activeCollapsibleFiltersCount(): number {
    return this.getCollapsibleFilters()
      .filter(filter => filter.selectedValues &&
      (Array.isArray(filter.selectedValues) ?
        filter.selectedValues.length > 0 :
        filter.selectedValues !== null && filter.selectedValues !== undefined))
      .length;
  }
  get filters() {
    return this._currentFilters;
  }
  @Input()
  set filters(filters) {
    if (this._previousFilters) {
      this.updateFiltersControls(this._previousFilters, filters);
    }
    this._previousFilters = _.cloneDeep(filters);
    this._currentFilters = _.cloneDeep(filters);
    setTimeout(() => {
      this.updateFilterButtonsPositionInGrid();
    });
  }

  ngOnInit() {
    this.filtersControls = new UntypedFormArray([]);
    this.filtersControls = this.populateFiltersToControls(this.filters);
    this.filtersControls?.addValidators(this.formValidatorFn);
  }

  ngAfterViewInit() {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'resize').pipe(
        startWith(''),
        takeUntil(this.unsubscribe$)
      ).subscribe(() => {
        this.updateFilterButtonsPositionInGrid();
      });
    });
  }

  trackByFn(index: number) {
    return index;
  }

  populateFiltersToControls(filters: AppFilter[]) {
    const updatedFilters = new UntypedFormArray([]);
    let selectedValues;
    filters.forEach((filter: AppFilter) => {
      if (filter.type === 'SELECT' || filter.type === 'SELECT_WITH_SWITCH') {
        selectedValues = filter.selectedValues ? filter.selectedValues.map(val => val.displayName) : [];
      } else {
        selectedValues = _.cloneDeep(filter.selectedValues);
      }
      updatedFilters.push(this.formBuilder.control(selectedValues));
    });
    return updatedFilters;
  }

  updateFiltersControls(previousFilters: AppFilter[], currentFilters: AppFilter[]) {
    currentFilters.forEach((currentFilter: AppFilter, index: number) => {

      if (!_.isEqual(currentFilter.selectedValues, previousFilters[index].selectedValues)) {
        let selectedValues;
        if (currentFilter.type === 'SELECT' || currentFilter.type === 'SELECT_WITH_SWITCH') {
          selectedValues = currentFilter.selectedValues ? currentFilter.selectedValues.map(val => val.displayName) : [];
        } else {
          selectedValues = _.cloneDeep(currentFilter.selectedValues);
        }
        this.filtersControls.at(index).setValue(selectedValues, { emitEvent: false });
      }

      // workaround bug that happened only on this filter
      if (currentFilter.id === 'publisherReportTimezone' &&
        this.filtersControls.at(index).value[0] !== currentFilter.selectedValues[0].displayName) {
        this.filtersControls.at(index).setValue(currentFilter.selectedValues[0].displayName, { emitEvent: false });
      }

      if (currentFilter.isValid && this.filtersControls.at(index).hasError('err')) {
        this.filtersControls.at(index).setErrors(null);
      }
      if (!currentFilter.isValid && !this.filtersControls.at(index).hasError('err')) {
        this.filtersControls.at(index).setErrors({ err: true });
      }

      if (currentFilter.isDisabled && !previousFilters[index].isDisabled) {
        this.filtersControls.at(index).disable({ emitEvent: false });
      }
      if (!currentFilter.isDisabled && previousFilters[index].isDisabled) {
        this.filtersControls.at(index).enable({ emitEvent: false });
      }
      if (currentFilter.isRequired && !previousFilters[index].isRequired) {
        this.filtersControls.at(index).setValidators([Validators.required]);
        this.filtersControls.at(index).markAsTouched();
        this.filtersControls.at(index).updateValueAndValidity();
      }
      if (!currentFilter.isRequired && previousFilters[index].isRequired) {
        this.filtersControls.at(index).setValidators(null);
        this.filtersControls.at(index).updateValueAndValidity();
      }
    });
  }

  onFilterChange(selectedValues, controlIndex: number) {
    this.filters[controlIndex].selectedValues = Array.isArray(selectedValues) ? [...selectedValues] : selectedValues;
    this.updateFilterDebounce(this.filters[controlIndex].id);
  }

  onFilterSearch(searchValue, controlIndex: number) {
    this.searchFilterDebounce(searchValue, this.filters[controlIndex].id);
  }

  onButtonClick(buttonId: string) {
    if (buttonId.includes('ApplyFiltersButton') && !this.filtersControls.valid) {
      this.sharedService.showNotification('error', 'Invalid filters', 'Please ensure filters are valid');
      return;
    }
    this.clickButtonDebounce(buttonId);
  }

  onDatePickerChange(datePickerState: any) {
    this.buttons.forEach(btn => {
      if (btn.id.includes('ReportApplyFiltersButton') || btn.id.includes('ReportDownloadCsvButton')) {
        btn.isDisabled = datePickerState.isDatePickerOpen;
      }
    });
  }

  updateFilterButtonsPositionInGrid() {
    // If collapsible filters exist, always put buttons on new row
    if (this.hasCollapsibleFilters) {
      this.setFilterButtonsColStart(1);
      return;
    }
    const appInputElements = this.appInputs?.toArray();
    const lastAppInput = appInputElements?.[appInputElements.length - 1];
    const filterButtonsEl = this.filterButtons?.nativeElement;
    const firstVisibleButton = filterButtonsEl?.children?.[0] as HTMLElement;
    // Early exit conditions - set to new row
    if (!appInputElements?.length || !this.filterButtons || !firstVisibleButton) {
      this.setFilterButtonsColStart(1);
      return;
    }
    // Calculate available space
    const lastInputRight = lastAppInput.nativeElement.getBoundingClientRect().right;
    const firstButtonLeft = firstVisibleButton.getBoundingClientRect().left;
    const availableSpace = firstButtonLeft - lastInputRight;
    // Set position based on available space (16px minimum gap)
    const hasEnoughSpace = availableSpace >= 16;
    this.setFilterButtonsColStart(hasEnoughSpace ? 'auto' : 1);
  }

  sortByChanged(i: number, value: SortOption): void {
    this._currentFilters[i].sortType = value;
    this.updateFilterDebounce(this.filters[i].id);
  }

  ngOnDestroy() {
    this.cdr.detach();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  isFilterCollapsible(filterId: string): boolean {
    return this.collapsibleFilters ? this.collapsibleFilters.includes(filterId) : false;
  }

  getCollapsibleFilters(): AppFilter[] {
    return this.filters?.filter(filter => this.isFilterCollapsible(filter.id)) || [];
  }

  getRegularFilters(): AppFilter[] {
    return this.filters?.filter(filter => !this.isFilterCollapsible(filter.id)) || [];
  }

  private setFilterButtonsColStart(value: string | number) {
    this.ngZone.runOutsideAngular(() => {
      this.filterButtonsColStart = value;
    });
  }

}
